// Moxfield sync — pure functions for CSV generation + card processing
// Port of public/moxfield-sync.js (framework-agnostic)

import { extractSourceInfo } from './url-parser.js';

/**
 * Check if a URL is a Moxfield binder
 */
export function isMoxfieldBinder(url) {
  if (!url) return false;
  const sourceInfo = extractSourceInfo(url);
  return sourceInfo?.source === 'moxfield' && sourceInfo?.type === 'binder';
}

/**
 * Extract binder ID from a Moxfield binder URL
 */
export function getBinderId(url) {
  if (!url) return null;
  const sourceInfo = extractSourceInfo(url);
  if (sourceInfo?.source === 'moxfield' && sourceInfo?.type === 'binder') {
    return sourceInfo.id;
  }
  return null;
}

/**
 * Map language code to Moxfield language name
 */
function mapLanguageCode(code) {
  const languageMap = {
    en: 'English',
    ja: 'Japanese',
    de: 'German',
    fr: 'French',
    it: 'Italian',
    es: 'Spanish',
    pt: 'Portuguese',
    ko: 'Korean',
    ru: 'Russian',
    zhs: 'Simplified Chinese',
    zht: 'Traditional Chinese',
  };
  return languageMap[code?.toLowerCase()] || 'English';
}

/**
 * Convert a card to Moxfield CSV row format
 * Headers: Count,Name,Edition,Collector Number,Foil,Condition,Language
 */
export function cardToMoxfieldCsvRow(card, quantityOverride) {
  const count = quantityOverride !== undefined ? quantityOverride : card.quantity || 1;
  const name = card.name || '';
  const edition = (card.set || '').toUpperCase();
  const collectorNumber = card.collectorNumber || '';
  const foil = card.isFoil ? 'foil' : '';
  const condition = 'Near Mint';
  const language = mapLanguageCode(card.lang || 'en');

  const escapedName =
    name.includes(',') || name.includes('"') ? `"${name.replace(/"/g, '""')}"` : name;

  return `${count},${escapedName},${edition},${collectorNumber},${foil},${condition},${language}`;
}

/**
 * Generate Moxfield-compatible CSV content
 */
export function generateMoxfieldCsv(cards) {
  const header = 'Count,Name,Edition,Collector Number,Foil,Condition,Language';
  if (!cards || cards.length === 0) return header;
  const rows = cards.map((card) => cardToMoxfieldCsvRow(card));
  return [header, ...rows].join('\n');
}

/**
 * Generate a unique key for a card (for exclusion tracking)
 */
export function getCardKey(card) {
  return `${card.scryfallId}_${card.isFoil}_${card.lang || 'en'}`;
}

/**
 * Generate combined Moxfield CSV with removals (negative) and additions (positive)
 */
export function generateCombinedCsv(cardsToRemove, cardsToAdd, excludedKeys = new Set()) {
  const header = 'Count,Name,Edition,Collector Number,Foil,Condition,Language';
  const rows = [];

  if (cardsToRemove && cardsToRemove.length > 0) {
    for (const card of cardsToRemove) {
      rows.push(cardToMoxfieldCsvRow(card, -(card.quantity || 1)));
    }
  }

  if (cardsToAdd && cardsToAdd.length > 0) {
    for (const card of cardsToAdd) {
      if (!excludedKeys.has(getCardKey(card))) {
        rows.push(cardToMoxfieldCsvRow(card));
      }
    }
  }

  return rows.length === 0 ? header : [header, ...rows].join('\n');
}

/**
 * Get cards the user traded away (to be removed from their binder)
 */
export function getCardsToRemove(trade, userSide) {
  const sideKey = `side${userSide}`;
  const cards = trade[sideKey]?.cards || [];
  return cards.filter((c) => c.status === 'trade' || !c.status);
}

/**
 * Get cards the user is receiving (to be added to their binder)
 */
export function getCardsToReceive(trade, userSide) {
  const oppositeSide = userSide === 'A' ? 'B' : 'A';
  const sideKey = `side${oppositeSide}`;
  const cards = trade[sideKey]?.cards || [];
  return cards.filter((c) => c.status === 'trade' || !c.status);
}

/**
 * Get the user's Moxfield binder URL from the trade (reconstructed to moxfield.com)
 */
export function getUserBinderUrl(trade, userSide) {
  const sideKey = `side${userSide}`;
  const binderUrl = trade[sideKey]?.binderUrl || null;
  if (!binderUrl) return null;

  const sourceInfo = extractSourceInfo(binderUrl);
  if (!sourceInfo || sourceInfo.source !== 'moxfield') return null;

  // Reconstruct the actual Moxfield URL
  const typeMap = { binder: 'binders', deck: 'decks', collection: 'collection' };
  const pathSegment = typeMap[sourceInfo.type] || sourceInfo.type;
  return `https://www.moxfield.com/${pathSegment}/${sourceInfo.id}`;
}
